import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';
import { useIntegrationOwners } from 'hooks/useIntegrationOwners';
import { useState } from 'react';
import { Button, Divider, IconButton, Input, Table } from 'rsuite';

import { showNotification } from '../../ShowNotification';

const { Column, HeaderCell, Cell } = Table;

interface IntegrationOwner {
    id: number;
    name: string;
    integration_owner_id: number;
}

interface EditCellProps {
    rowData?: IntegrationOwner;
    dataKey?: keyof IntegrationOwner;
    [key: string]: any;
}

interface ActionCellProps {
    rowData?: IntegrationOwner;
    [key: string]: any;
}

export function IntegrationOwners() {
    const [name, setName] = useState('');
    const [integration_owner_id, setIntegration_owner_id] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editIntegrationOwnerId, setEditIntegrationOwnerId] = useState('');

    const {
        integrationOwners,
        createIntegrationOwner,
        updateIntegrationOwner,
        deleteIntegrationOwner,
        refetch,
    } = useIntegrationOwners();

    const addIntegrationOwner = () => {
        if (name === '' || integration_owner_id === null) {
            showNotification({
                type: 'error',
                body: 'Name and companyId must be set',
                header: 'Data missing!',
            });
            return;
        }
        createIntegrationOwner.mutate({
            name,
            integration_owner_id: Number(integration_owner_id),
        });

        // Clear form after successful creation
        setName('');
        setIntegration_owner_id('');
        refetch();
    };

    const handleEdit = (rowData: IntegrationOwner) => {
        setEditingId(rowData.id);
        setEditName(rowData.name);
        setEditIntegrationOwnerId(rowData.integration_owner_id.toString());
        refetch();
    };

    const handleSave = () => {
        if (editName === '' || editIntegrationOwnerId === '') {
            showNotification({
                type: 'error',
                body: 'Name and companyId must be set',
                header: 'Data missing!',
            });
            return;
        }

        if (editingId !== null) {
            updateIntegrationOwner.mutate({
                id: editingId,
                name: editName,
                integration_owner_id: Number(editIntegrationOwnerId),
            });
        } else {
            showNotification({
                type: 'error',
                body: 'No item selected for editing.',
                header: 'Edit error!',
            });
        }

        setEditingId(null);
        setEditName('');
        setEditIntegrationOwnerId('');
        refetch();
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditName('');
        setEditIntegrationOwnerId('');
    };

    const handleDelete = (rowData: IntegrationOwner) => {
        if (window.confirm(`Are you sure you want to delete "${rowData.name}"?`)) {
            deleteIntegrationOwner.mutate(rowData.id);
            refetch();
        }
    };

    const EditCell = ({ rowData, dataKey, ...props }: EditCellProps) => {
        const isEditing = editingId === rowData?.id;

        if (!isEditing || !rowData || !dataKey) {
            return <Cell {...props}>{rowData && dataKey ? rowData[dataKey] : ''}</Cell>;
        }

        if (dataKey === 'name') {
            return (
                <Cell {...props}>
                    <Input value={editName} onChange={setEditName} size="sm" />
                </Cell>
            );
        }

        if (dataKey === 'integration_owner_id') {
            return (
                <Cell {...props}>
                    <Input
                        value={editIntegrationOwnerId}
                        onChange={setEditIntegrationOwnerId}
                        size="sm"
                    />
                </Cell>
            );
        }

        return <Cell {...props}>{dataKey ? rowData[dataKey] : ''}</Cell>;
    };

    const ActionCell = ({ rowData, ...props }: ActionCellProps) => {
        const isEditing = editingId === rowData?.id;

        if (!rowData) {
            return <Cell {...props}></Cell>;
        }

        return (
            <Cell {...props} style={{ padding: '6px' }}>
                {isEditing ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <Button size="xs" appearance="primary" onClick={handleSave}>
                            Save
                        </Button>
                        <Button size="xs" appearance="subtle" onClick={handleCancel}>
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                            icon={<EditIcon />}
                            size="xs"
                            onClick={() => handleEdit(rowData)}
                            appearance="subtle"
                        />
                        <IconButton
                            icon={<TrashIcon />}
                            size="xs"
                            onClick={() => handleDelete(rowData)}
                            appearance="subtle"
                            color="red"
                        />
                    </div>
                )}
            </Cell>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
            <h3>Integration Owners</h3>
            <div>
                <Input placeholder="Name" value={name} onChange={value => setName(value)} />
            </div>
            <div>
                <Input
                    placeholder="Procountor Company Id"
                    value={integration_owner_id}
                    onChange={value => setIntegration_owner_id(value)}
                />
            </div>
            <div>
                <Button onClick={addIntegrationOwner} appearance="primary">
                    Add
                </Button>
            </div>
            <Divider />

            <Table
                height={200}
                data={integrationOwners || []}
                onRowClick={data => {
                    console.log(data);
                }}
            >
                <Column width={60} align="center" fixed>
                    <HeaderCell>ID</HeaderCell>
                    <Cell dataKey="id" />
                </Column>

                <Column width={200} flexGrow={1}>
                    <HeaderCell>Name</HeaderCell>
                    <EditCell dataKey="name" />
                </Column>

                <Column width={200}>
                    <HeaderCell>Company ID</HeaderCell>
                    <EditCell dataKey="integration_owner_id" />
                </Column>

                <Column width={120} fixed="right">
                    <HeaderCell>Actions</HeaderCell>
                    <ActionCell />
                </Column>
            </Table>
            <code>
                Integration owner is a company, like Circles Consulting Oy, that has its own
                Procountor credentials.
            </code>
            <code>
                Each account has their own customers and my.circles needs to be able to send
                invoices to correct Procountor accounts.
            </code>
            <code>
                Here we store the Company IDs provided by Procountor, and the API Credentials that
                correspond these ids are in AWS Parameter Store.
            </code>
            <code>
                The companyId is returned from this endpoint:
                https://api.procountor.com/latest/api/sessioninfo
            </code>
            <code>
                API credentials for your convenience can be found in your Google Drive, just search
                for procountor.
            </code>
        </div>
    );
}
